import { useState } from "react";

interface CircularScaleQuestionProps {
  options: string[];
  currentAnswer: string;
  onAnswer: (answer: string) => void;
}

const CircularScaleQuestion = ({ options, currentAnswer, onAnswer }: CircularScaleQuestionProps) => {
  const [hoveredValue, setHoveredValue] = useState<string | null>(null);
  
  // Create segments for the dial (numbers 1-10)
  const totalSegments = 10;
  const anglePerSegment = 360 / totalSegments;
  
  const getSegmentColor = (value: number) => {
    // Smooth linear transition from green (120°) to red (0°)
    const hue = 120 - ((value - 1) / 9) * 120;
    return `hsl(${hue}, 65%, 55%)`;
  };

  const renderDialSegment = (value: number, index: number) => {
    const angle = index * anglePerSegment;
    const startAngle = angle - anglePerSegment / 2;
    const endAngle = angle + anglePerSegment / 2;
    
    const isSelected = currentAnswer === value.toString();
    const isHovered = hoveredValue === value.toString();
    
    const outerRadius = 42;
    const innerRadius = 20;
    const centerX = 50;
    const centerY = 50;
    
    const startAngleRad = (startAngle - 90) * Math.PI / 180;
    const endAngleRad = (endAngle - 90) * Math.PI / 180;
    
    // Outer arc points
    const x1 = centerX + outerRadius * Math.cos(startAngleRad);
    const y1 = centerY + outerRadius * Math.sin(startAngleRad);
    const x2 = centerX + outerRadius * Math.cos(endAngleRad);
    const y2 = centerY + outerRadius * Math.sin(endAngleRad);
    
    // Inner arc points
    const x3 = centerX + innerRadius * Math.cos(endAngleRad);
    const y3 = centerY + innerRadius * Math.sin(endAngleRad);
    const x4 = centerX + innerRadius * Math.cos(startAngleRad);
    const y4 = centerY + innerRadius * Math.sin(startAngleRad);
    
    const largeArcFlag = anglePerSegment > 180 ? 1 : 0;
    
    const pathData = [
      `M ${x1} ${y1}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      `L ${x3} ${y3}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
      'Z'
    ].join(' ');

    // Text position
    const textAngle = (angle - 90) * Math.PI / 180;
    const textRadius = 31;
    const textX = centerX + textRadius * Math.cos(textAngle);
    const textY = centerY + textRadius * Math.sin(textAngle);
    
    return (
      <g key={value}>
        <path
          d={pathData}
          fill={getSegmentColor(value)}
          stroke="hsl(var(--border))"
          strokeWidth="1"
          className={`cursor-pointer transition-all duration-300 ${
            isSelected ? 'brightness-110 drop-shadow-md' : 
            isHovered ? 'brightness-105' : 'opacity-90 hover:opacity-100'
          }`}
          style={{
            transformOrigin: '50% 50%',
            filter: isSelected ? 'drop-shadow(0 2px 8px hsl(var(--primary) / 0.3))' : 'none'
          }}
          onMouseEnter={() => setHoveredValue(value.toString())}
          onMouseLeave={() => setHoveredValue(null)}
          onClick={() => onAnswer(value.toString())}
        />
        <text
          x={textX}
          y={textY}
          textAnchor="middle"
          dominantBaseline="central"
          className={`pointer-events-none font-semibold text-sm ${
            isSelected ? 'fill-white drop-shadow-sm' : 'fill-slate-700'
          }`}
        >
          {value}
        </text>
      </g>
    );
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex justify-center">
        <div className="relative bg-card/50 rounded-full p-4 border border-border/50 shadow-sm">
          <svg
            width="240"
            height="240"
            viewBox="0 0 100 100"
            className="w-[240px] h-[240px] md:w-[280px] md:h-[280px]"
          >
            {/* Background circle */}
            <circle
              cx="50"
              cy="50"
              r="46"
              fill="hsl(var(--background))"
              stroke="hsl(var(--border))"
              strokeWidth="0.5"
            />
            
            {/* Subtle grid lines for clinical appearance */}
            <defs>
              <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                <path d="M 10 0 L 0 0 0 10" fill="none" stroke="hsl(var(--border))" strokeWidth="0.2" opacity="0.3"/>
              </pattern>
            </defs>
            <circle cx="50" cy="50" r="45" fill="url(#grid)" opacity="0.1"/>
            
            {/* Render segments */}
            {Array.from({ length: 10 }, (_, i) => i + 1).map((value, index) => 
              renderDialSegment(value, index)
            )}
            
            {/* Center circle with subtle gradient */}
            <defs>
              <radialGradient id="centerGradient" cx="50%" cy="30%">
                <stop offset="0%" stopColor="hsl(var(--background))" />
                <stop offset="100%" stopColor="hsl(var(--muted))" />
              </radialGradient>
            </defs>
            <circle
              cx="50"
              cy="50"
              r="18"
              fill="url(#centerGradient)"
              stroke="hsl(var(--border))"
              strokeWidth="1.5"
            />
            
            {/* Selected value indicator in center */}
            {currentAnswer && (
              <>
                <text
                  x="50"
                  y="47"
                  textAnchor="middle"
                  dominantBaseline="central"
                  className="font-bold text-lg fill-primary"
                >
                  {currentAnswer}
                </text>
                <text
                  x="50"
                  y="56"
                  textAnchor="middle"
                  dominantBaseline="central"
                  className="font-medium text-xs fill-muted-foreground"
                >
                  /10
                </text>
              </>
            )}
          </svg>
        </div>
      </div>
      
      <div className="text-center space-y-3">
        <div className="flex justify-between items-center text-xs md:text-sm text-muted-foreground max-w-xs md:max-w-sm mx-auto px-2">
          <div className="flex items-center space-x-1.5">
            <div className="w-2 h-2 rounded-full bg-emerald-400 border border-emerald-500"></div>
            <span>Minimal</span>
          </div>
          <div className="flex items-center space-x-1.5">
            <div className="w-2 h-2 rounded-full bg-amber-400 border border-amber-500"></div>
            <span>Moderate</span>
          </div>
          <div className="flex items-center space-x-1.5">
            <div className="w-2 h-2 rounded-full bg-red-400 border border-red-500"></div>
            <span>Severe</span>
          </div>
        </div>
        
        {currentAnswer && (
          <div className="bg-secondary/30 rounded-lg px-3 py-2 border border-border/30">
            <p className="text-sm text-foreground">
              Selected Impact Level: <span className="font-semibold text-primary">{currentAnswer}/10</span>
            </p>
          </div>
        )}
        
        <p className="text-xs text-muted-foreground max-w-sm mx-auto leading-relaxed">
          Rate how much this symptom impacts your daily activities and quality of life
        </p>
      </div>
    </div>
  );
};

export default CircularScaleQuestion;